import _ from "lodash";
import dayjs from "dayjs";

// Minutos a partir de los cuales una unidad se considera en riesgo / adelantada.
// "Quemada" siempre es diferencia > tolerancia del checkpoint, igual que el motor.
export const MIN_RIESGO = 3;
export const MIN_ADELANTO = -3;

export const getEstado = (desv, tolerancia) => {
  if (desv === null || desv === undefined) return "sin";
  if (desv > tolerancia) return "que";
  if (desv >= MIN_RIESGO) return "rie";
  if (desv <= MIN_ADELANTO) return "ade";
  return "tie";
};

// Semaforo del tablero. Las claves son las de getEstado(); "quemada" es diferencia > tolerancia del checkpoint.
export const SEV = {
  ade: { label: "Adelantada", color: "#4b9ddb", text: "#1d6fa8" },
  tie: { label: "En tiempo", color: "#0ab39c", text: "#0a7d6f" },
  rie: { label: "En riesgo", color: "#f7b84b", text: "#8a5d00" },
  que: { label: "Quemada", color: "#f06548", text: "#b4341c" },
  sin: { label: "Sin GPS", color: "#5d6470", text: "#5d6470" },
};

export const ORDENES = [
  { key: "llegada", label: "Llegada más próxima" },
  { key: "desviacion", label: "Mayor desviación" },
  { key: "ramal", label: "Ramal" },
];

export const LEYENDA = [
  { color: SEV.ade.color, label: `Adelantada · ${Math.abs(MIN_ADELANTO)} min o más` },
  { color: SEV.tie.color, label: `En tiempo · ±${MIN_RIESGO - 1} min` },
  { color: SEV.rie.color, label: `En riesgo · ${MIN_RIESGO} min hasta la tolerancia` },
  { color: SEV.que.color, label: "Quemada · más que la tolerancia" },
  { color: SEV.sin.color, label: "Sin lecturas GPS" },
];

const TIEMPOS_VISIBLES = 5;

export const deltaTxt = (d) => (d === 0 ? "±0" : d > 0 ? `+${d}` : String(d));

const hora = (date, minutos) => dayjs(date).add(minutos, "minute").format("HH:mm");

// Checkpoint ya pasado con lectura, sin lectura o por pasar (estimado con la desviacion actual)
const buildTiempo = (salida, cp, i, hechas, desv) => {
  const base = { key: `${cp.position}-${i}`, nombre: cp.nombre, prog: cp.prog ?? "—" };
  if (i >= hechas) {
    return {
      ...base,
      tipo: "futura",
      real: cp.minuto != null ? hora(salida.date, cp.minuto + desv) : "·",
      delta: "est.",
      realColor: "#5d6470",
      deltaColor: "#5d6470",
    };
  }
  if (cp.diferencia == null) {
    return { ...base, tipo: "sinLectura", real: "—", delta: "sin lectura", realColor: "#5d6470", deltaColor: "#5d6470" };
  }
  const estado = getEstado(cp.diferencia, cp.tolerancy);
  return {
    ...base,
    tipo: estado === "que" ? "quemada" : "pasada",
    real: cp.real,
    delta: deltaTxt(cp.diferencia),
    realColor: "#212529",
    deltaColor: SEV[estado].text,
  };
};

export const buildRow = (salida, today) => {
  const checkpoints = salida?.checkpoints ?? [];
  const n = checkpoints.length;
  const hechas = _.findLastIndex(checkpoints, (cp) => cp.real != null) + 1;
  const ultimo = hechas > 0 ? checkpoints[hechas - 1] : null;

  const desv = ultimo?.diferencia ?? null;
  const sinDatos = desv === null;
  const d = sinDatos ? 0 : desv;
  const estado = getEstado(desv, ultimo?.tolerancy ?? 0);
  const sev = SEV[estado];

  const total = checkpoints[n - 1]?.minuto || 1;
  const pct = hechas > 0 ? _.clamp(Math.round((ultimo.minuto / total) * 100), 1, 99) : 1;

  const llegadaProg = dayjs(salida.date).add(total, "minute");
  const llegadaProy = llegadaProg.add(d, "minute");

  const ini = Math.max(0, Math.min(hechas - 3, n - TIEMPOS_VISIBLES));
  const tiempos = checkpoints
    .slice(ini, ini + TIEMPOS_VISIBLES)
    .map((cp, j) => ({ ...buildTiempo(salida, cp, ini + j, hechas, d), siguiente: ini + j === hechas }));

  return {
    id: salida.id,
    salida,
    unidad: salida.unidad ?? "-",
    ramalId: salida.ramal_id,
    ramalNombre: salida.ramal_nombre ?? "-",
    horaSalida: salida.hora,
    estado,
    sev,
    sinDatos,
    desv,
    desvLabel: sinDatos ? "s/d" : deltaTxt(d),
    pct,
    pctLabel: `${hechas}/${n} geocercas`,
    pulse: estado === "que",
    llegadaProg: llegadaProg.format("HH:mm"),
    llegadaProy: llegadaProy.format("HH:mm"),
    restanMin: Math.max(0, llegadaProy.diff(dayjs(today), "minute")),
    restanParadas: n - hechas,
    lecturas: `${checkpoints.slice(0, hechas).filter((cp) => cp.diferencia != null).length}/${hechas} geocercas`,
    ticks: checkpoints.map((cp, i) => ({
      key: `${cp.position}-${i}`,
      left: Math.min(99.6, ((cp.minuto ?? 0) / total) * 100),
      color: i < hechas ? (cp.diferencia == null ? "#9296a3" : sev.color) : "#d3d8de",
    })),
    tiempos,
    paradas: checkpoints.map((cp, i) => buildTiempo(salida, cp, i, hechas, d)),
    _proy: llegadaProy.valueOf(),
  };
};

export const buildKpis = (rows) => {
  const c = _.countBy(rows, "estado");
  const validas = rows.filter((r) => !r.sinDatos);
  const prom = validas.length ? _.meanBy(validas, "desv") : 0;

  return [
    { key: "total", label: "Por concluir", value: rows.length, unit: "salidas", color: "#212529" },
    { key: "tie", label: "En tiempo", value: (c.tie ?? 0) + (c.ade ?? 0), unit: "unidades", color: SEV.tie.color },
    { key: "rie", label: "En riesgo", value: c.rie ?? 0, unit: "unidades", color: SEV.rie.color },
    { key: "que", label: "Quemadas", value: c.que ?? 0, unit: "unidades", color: SEV.que.color },
    { key: "sin", label: "Sin lecturas", value: c.sin ?? 0, unit: "unidades", color: SEV.sin.color },
    {
      key: "prom",
      label: "Desv. promedio",
      value: `${prom > 0 ? "+" : ""}${prom.toFixed(1)}`,
      unit: "min",
      color: prom >= MIN_RIESGO ? SEV.rie.color : SEV.tie.color,
    },
  ];
};

export const sortRows = (rows, orden) => {
  if (orden === "desviacion") return _.orderBy(rows, [(r) => (r.sinDatos ? -Infinity : r.desv)], ["desc"]);
  if (orden === "ramal") return _.sortBy(rows, ["ramalNombre", "_proy"]);
  return _.sortBy(rows, "_proy");
};
